import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JsBarcode from 'jsbarcode';
import { formatItemDisplayName } from '../../sales/utils/formatItemDisplayName';

/**
 * Generate a PNG data URL for a barcode.
 */
function generateBarcodeDataUrl(text) {
  if (!text) return null;
  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, String(text).trim(), {
      format: 'CODE128',
      width: 2,
      height: 36,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
}

/**
 * Format date for display (dd/MM/yyyy).
 */
function formatDate(value, dateFormat = 'dd/MM/yyyy') {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  switch (dateFormat) {
    case 'MM/dd/yyyy':
      return `${mm}/${dd}/${yyyy}`;
    case 'yyyy-MM-dd':
      return `${yyyy}-${mm}-${dd}`;
    case 'dd-MM-yyyy':
      return `${dd}-${mm}-${yyyy}`;
    case 'dd/MM/yyyy':
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
}

/**
 * Fetch image from URL and return as data URL (for embedding in PDF).
 */
async function fetchLogoAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Export a purchase order note to PDF.
 */
export async function exportPurchaseOrderPdf(poNote, company, options = {}) {
  let logoDataUrl = options.logoDataUrl;
  if (!logoDataUrl && company?.logoUrl) {
    logoDataUrl = await fetchLogoAsDataUrl(company.logoUrl);
  }

  const {
    filename = (poNote.poNumber || 'purchase-order').replace(/\s+/g, '-'),
    itemFormat = company?.printItemNameFormat || 'name_sku',
    save = true,
  } = options;

  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value ?? 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
        maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatItemName = (item) =>
    formatItemDisplayName(
      {
        name: item.product?.name,
        sku: item.product?.sku,
        brand: item.product?.brand,
      },
      itemFormat
    );

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const colorPrimary = [15, 23, 42]; // Slate Dark
  const colorAccent = [78, 205, 196]; // Teal primary brand
  const colorTextDark = [30, 41, 59];
  const colorTextMuted = [100, 116, 139];
  const colorBgCard = [248, 250, 252];
  const colorBorder = [226, 232, 240];

  let y = 14;

  // Decorative Top Accent Bar
  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(0, 0, pageWidth, 3, 'F');

  // --- BRANDING SECTION (Header) ---
  const logoSize = 18;
  const logoPadding = 2;

  if (logoDataUrl) {
    try {
      const format = logoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, logoSize + logoPadding * 2, logoSize + logoPadding * 2, 1.5, 1.5, 'FD');
      doc.addImage(logoDataUrl, format, margin + logoPadding, y + logoPadding, logoSize, logoSize);
    } catch (e) {
      // ignore
    }
  }

  const companyX = logoDataUrl ? margin + logoSize + logoPadding * 2 + 4 : margin;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
  doc.text(company?.name || 'Company Name', companyX, y + 4);

  let companyInfoY = y + 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);

  const companyTextWidth = pageWidth * 0.45;
  if (company?.address) {
    const addrLines = doc.splitTextToSize(company.address, companyTextWidth);
    addrLines.forEach((line) => {
      doc.text(line, companyX, companyInfoY);
      companyInfoY += 4;
    });
  }

  if (company?.trn) {
    doc.setFont('helvetica', 'bold');
    doc.text(`TRN: ${company.trn}`, companyX, companyInfoY);
    companyInfoY += 4;
  }

  // Right Column: Title & Metadata Card
  const metaCardWidth = 70;
  const metaCardX = pageWidth - margin - metaCardWidth;
  const metaCardY = y;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.text('PURCHASE ORDER', pageWidth - margin, y + 4, { align: 'right' });

  let metaRows = 3;
  if (poNote.expectedDeliveryDate) metaRows += 1;
  const barcodeDataUrl = generateBarcodeDataUrl(poNote.poNumber);
  const barcodeHeight = barcodeDataUrl ? 7.5 : 0;
  const metaCardHeight = 4 + metaRows * 4.5 + barcodeHeight + 3;

  doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(metaCardX, metaCardY + 7, metaCardWidth, metaCardHeight, 2, 2, 'FD');

  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(metaCardX, metaCardY + 7, 1.5, metaCardHeight, 'F');

  let metaY = metaCardY + 11.5;
  doc.setFontSize(8);

  const drawMetaRow = (label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text(label, metaCardX + 4, metaY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);
    doc.text(value, pageWidth - margin - 4, metaY, { align: 'right' });
    metaY += 4.5;
  };

  drawMetaRow('PO Number:', poNote.poNumber || '-');
  if (barcodeDataUrl) {
    doc.addImage(barcodeDataUrl, 'PNG', metaCardX + 4, metaY, metaCardWidth - 8, 6);
    metaY += 7.5;
  }
  drawMetaRow('Order Date:', formatDate(poNote.orderDate, dateFormat));
  drawMetaRow('Status:', (poNote.status || 'draft').toUpperCase());

  if (poNote.expectedDeliveryDate) {
    drawMetaRow('Expected Date:', formatDate(poNote.expectedDeliveryDate, dateFormat));
  }

  y = Math.max(companyInfoY + 6, metaCardY + 7 + metaCardHeight + 6);

  // --- VENDOR / SUPPLIER SECTION ---
  const supplierCardWidth = pageWidth - margin * 2;
  const supplierCardHeight = 28;
  doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, supplierCardWidth, supplierCardHeight, 2, 2, 'FD');

  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(margin, y, 1.5, supplierCardHeight, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.text('VENDOR / SUPPLIER', margin + 6, y + 6);

  let supplierY = y + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
  doc.text(poNote.supplier?.name || '-', margin + 6, supplierY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);

  let supplierInfoStr = '';
  if (poNote.supplier?.address) supplierInfoStr += `${poNote.supplier.address}  |  `;
  if (poNote.supplier?.phone) supplierInfoStr += `Phone: ${poNote.supplier.phone}  |  `;
  if (poNote.supplier?.email) supplierInfoStr += `Email: ${poNote.supplier.email}  |  `;
  if (poNote.supplier?.trn) supplierInfoStr += `TRN: ${poNote.supplier.trn}`;
  if (supplierInfoStr.endsWith('  |  ')) supplierInfoStr = supplierInfoStr.slice(0, -5);

  supplierY += 5;
  const supplierLines = doc.splitTextToSize(supplierInfoStr || '-', supplierCardWidth - 12);
  doc.text(supplierLines, margin + 6, supplierY);

  y += supplierCardHeight + 8;

  // --- ITEMS TABLE ---
  const items = poNote.items || [];
  const tableHeaders = ['#', 'Item Description', 'Qty', 'Unit', 'Unit Price', 'Discount', 'VAT', 'Total'];

  const tableRows = items.map((it, idx) => [
    String(idx + 1),
    formatItemName(it),
    Number(it.quantity ?? 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)),
    it.unit || 'pcs',
    formatAmount(it.unitPrice),
    formatAmount(it.discount ?? 0),
    formatAmount(it.vatAmount ?? 0),
    formatAmount(it.total ?? 0),
  ]);

  autoTable(doc, {
    startY: y,
    head: [tableHeaders],
    body: tableRows,
    margin: { top: 15, bottom: 15, left: margin, right: margin },
    styles: { fontSize: 8, font: 'helvetica', cellPadding: 3.5, textColor: colorTextDark },
    headStyles: { fillColor: colorPrimary, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [252, 253, 254] },
    tableLineColor: colorBorder,
    tableLineWidth: 0.1,
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { halign: 'left' },
      2: { halign: 'right', cellWidth: 20 },
      3: { halign: 'center', cellWidth: 15 },
      4: { halign: 'right', cellWidth: 24 },
      5: { halign: 'right', cellWidth: 22 },
      6: { halign: 'right', cellWidth: 20 },
      7: { halign: 'right', cellWidth: 26 },
    },
  });

  let cardRows = 4;
  if (Number(poNote.roundOff)) cardRows += 1;
  const totalsCardHeight = 4 + cardRows * 5 - 1;
  const targetY = pageHeight - 16 - totalsCardHeight;
  y = doc.lastAutoTable.finalY + 6;

  if (y > targetY) {
    doc.addPage();
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(0, 0, pageWidth, 3, 'F');
    y = targetY;
  } else {
    y = targetY;
  }

  // Left Column: Notes & Instructions
  const leftColWidth = pageWidth * 0.45;
  let notesY = y;
  if (poNote.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text('Notes / Instructions', margin, notesY);

    notesY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(colorTextDark[0], colorTextDark[1], colorTextDark[2]);
    const noteLines = doc.splitTextToSize(poNote.notes, leftColWidth - 4);

    const lineHt = Math.max(12, noteLines.length * 4);
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(margin, notesY - 3, 1, lineHt, 'F');

    let noteLineY = notesY;
    noteLines.forEach((line) => {
      doc.text(line, margin + 3, noteLineY);
      noteLineY += 4;
    });
  }

  // Right Column: Totals Card
  const totalsCardWidth = 65;
  const totalsCardX = pageWidth - margin - totalsCardWidth;
  let totalsY = y;

  doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(totalsCardX, totalsY, totalsCardWidth, totalsCardHeight, 1.5, 1.5, 'FD');

  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(totalsCardX, totalsY, 1.2, totalsCardHeight, 'F');

  totalsY += 4;
  doc.setFontSize(8);

  const drawTotalsRow = (label, value, isBold = false) => {
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(isBold ? colorPrimary[0] : colorTextDark[0], isBold ? colorPrimary[1] : colorTextDark[1], isBold ? colorPrimary[2] : colorTextDark[2]);
    doc.text(label, totalsCardX + 3, totalsY);
    doc.text(value, pageWidth - margin - 3, totalsY, { align: 'right' });
    totalsY += 5;
  };

  drawTotalsRow('Subtotal:', formatAmount(poNote.subtotal));
  drawTotalsRow('Discount:', formatAmount(poNote.discountTotal));
  drawTotalsRow('VAT:', formatAmount(poNote.vatTotal));

  if (Number(poNote.roundOff)) {
    drawTotalsRow('Round Off:', formatAmount(poNote.roundOff));
  }

  doc.setFillColor(239, 246, 255);
  doc.rect(totalsCardX + 1.2, totalsY - 3.5, totalsCardWidth - 1.5, 6, 'F');

  doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
  doc.setLineWidth(0.3);
  doc.line(totalsCardX + 3, totalsY - 2, pageWidth - margin - 3, totalsY - 2);
  totalsY += 1;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.text('Grand Total:', totalsCardX + 3, totalsY);
  doc.text(formatAmount(poNote.grandTotal), pageWidth - margin - 3, totalsY, { align: 'right' });

  // Footers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
    doc.text(company?.name || '', margin, pageHeight - 8);
    doc.text('Purchase Order Document', pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
  if (save) {
    doc.save(safeName);
    return undefined;
  }
  return doc.output('arraybuffer');
}

/**
 * Share purchase order as PDF.
 */
export async function sharePurchaseOrderPdf(poNote, company, options = {}) {
  const filename = (options.filename ?? (poNote.poNumber || 'purchase-order').replace(/\s+/g, '-')).replace(
    /\s+/g,
    '-'
  );
  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  const arraybuffer = await exportPurchaseOrderPdf(poNote, company, { ...options, save: false });
  if (!arraybuffer) return;
  const blob = new Blob([arraybuffer], { type: 'application/pdf' });

  let file = null;
  try {
    file = new File([blob], safeName, { type: 'application/pdf' });
  } catch (e) {
    // Ignore
  }

  const canShareFiles =
    typeof navigator !== 'undefined' &&
    !!navigator.share &&
    !!navigator.canShare &&
    !!file &&
    navigator.canShare({ files: [file] });

  if (canShareFiles) {
    try {
      await navigator.share({
        title: `Purchase Order ${poNote.poNumber || ''}`,
        text: company?.name ? `Purchase Order from ${company.name}` : 'Purchase Order',
        files: [file],
      });
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
  }

  try {
    const isWebView = typeof navigator !== 'undefined' && navigator.userAgent.match(/FBAN|FBAV|Instagram|LinkedInApp|WhatsApp/i);
    if (!isWebView) {
      await exportPurchaseOrderPdf(poNote, company, { ...options, filename, save: true });
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blob);
    }
  } catch (e) {
    await exportPurchaseOrderPdf(poNote, company, { ...options, filename, save: true });
  }
}
